import React from 'react';
import { CornerDownRight, Sparkles, HelpCircle } from 'lucide-react';

/**
 * SelectionTrace — the one-line answer to "why did this come up".
 *
 * Sits above each card in the recent-display list. The panel picks by hopping
 * between semantically related quotes, so the useful thing to say about a
 * display is almost never a property of the quote itself: it is what came
 * before it, and whether the two are related. That is what this line says.
 *
 * Deliberately in words. The cosine and the weight are on the row's title
 * attribute rather than on its face — a list of forty numbers between 0.6 and
 * 0.9 is not an explanation, it is a spreadsheet, and the number only becomes
 * interesting once you already believe the sentence.
 */

/** First few words of a quote, enough to recognise it in a list you scrolled past. */
const snippet = (text, max = 68) => {
  const flat = (text || '').replace(/\s+/g, ' ').trim();
  if (!flat) return 'an untitled quote';
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
};

const fmtGap = (hours) => {
  if (hours == null) return null;
  if (hours < 1) return 'minutes earlier';
  if (hours < 48) return `${Math.round(hours)}h earlier`;
  return `${Math.round(hours / 24)} days earlier`;
};

/** What the mode means, in the terms the walk actually works in. */
const MODES = {
  hop: {
    Icon: CornerDownRight,
    tone: 'text-blue-600 dark:text-blue-400',
    label: 'Followed on from',
  },
  restart: {
    Icon: Sparkles,
    tone: 'text-amber-600 dark:text-amber-400',
    label: 'Started fresh after',
  },
  unknown: {
    Icon: HelpCircle,
    tone: 'text-gray-400 dark:text-gray-500',
    label: 'No reading on this one',
  },
};

const SelectionTrace = ({ selection }) => {
  if (!selection) return null;

  const { mode, similarity, weight, anchor } = selection;
  const { Icon, tone, label } = MODES[mode] || MODES.unknown;

  // Every reason this line can be vague, spelled out on hover rather than
  // hidden: "unknown" covers three different situations and they are not the
  // same problem to go and fix.
  const detail = [
    similarity != null ? `cosine ${similarity.toFixed(3)} against the previous display` : null,
    !anchor ? 'nothing displayed before it inside this window' : null,
    similarity == null && anchor ? 'one of the two has no vector yet — rerun embed_quotes.py' : null,
    weight?.bucket === 'recent'
      ? `on cooldown when it was drawn (last shown ${fmtGap(weight.hours_since_last)}), so the walk could not have hopped to it`
      : null,
    weight?.hours_since_last == null ? 'first time on the panel' : null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      className="flex items-start gap-1.5 mb-1 px-1 text-xs text-gray-500 dark:text-gray-400"
      title={detail || undefined}
    >
      <Icon size={13} className={`flex-shrink-0 mt-0.5 ${tone}`} />
      <span className="min-w-0">
        <span className={tone}>{label}</span>
        {anchor && (
          <>
            {' '}
            <span className="italic">“{snippet(anchor.content)}”</span>
            {anchor.author && <span className="not-italic"> — {anchor.author}</span>}
          </>
        )}
        {weight?.bucket === 'recent' && (
          <span className="ml-1 text-amber-600 dark:text-amber-400">
            · repeat, {fmtGap(weight.hours_since_last)}
          </span>
        )}
      </span>
    </div>
  );
};

export default SelectionTrace;
